import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import ReadOnlyInputBasic from "../../../../components/input/ReadOnlyInputBasic";
import {createUser, nameIsDuplicate, usernameIsDuplicate} from "../../../../api/user/UserAccountService";
import {DetailSummaryButton} from "../../../../components/detailLayout";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import Th_title from "../../../../components/Th_title";
import {apiLoading, skeleton} from "../../../../redux/slice/apiSlice";
import {isAdmin, isEmpty, isSystem} from "../../../../components/commonmethod/CommonMethod";
import {CacheType, userAuth} from "../../../../constant/CodeSpecNum";
import {handleActiveError} from "../../../../components/sonner/ToastBasic";
import JellyCheckBox from "../../../../components/search/JellyCheckBox";
import {onCreateClickListener} from "../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../components/selector/Selector";
import {inputOnChange} from "../../../../components/commonmethod/simmsInputMethod";
import SimmsInput from "../../../../components/input/SimmsInput";
import {SimmsRadio} from "../../../../components/input/SimmsRadio";
import {dateOnChange} from "../../../../components/commonmethod/simmsDateMethod";
import SimmsDatePicker from "../../../../components/datepicker/SimmsDatePicker";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import {emailReceiveAuth, serviceReceiveAuth} from "../userAccountAuthOptions";
import "../userAccountEditForm.css";
import UserAccountFormTable from "./UserAccountFormTable";
import UserAccountProfileImageField, {UserAccountMediaRemoveButton} from "./UserAccountProfileImageField";
import SignaturePadModal from "../../../../components/modal/SignaturePadModal";
import {uploadImageSign} from "../../../../api/file/FileService";

function duplicateCheckBtnClass(state) {
    const base = "user-account-edit-form__inline-btn";
    return state === "passed"
        ? `${base} user-account-edit-form__inline-btn--passed`
        : `${base} user-account-edit-form__inline-btn--pending`;
}

function EditSection({title, children}) {
    return (
        <section className="user-account-edit-form__section">
            <h3 className="user-account-edit-form__section-title">{title}</h3>
            <div className="user-account-edit-form__section-body">{children}</div>
        </section>
    );
}

const REQUIRED_FIELDS = ["company", "department", "userGroup", "name", "username", "password", "email"];

const UserAccountCreateForm = forwardRef(function UserAccountCreateForm(
    {embedded = false, hideActions = false, submitLabel = "등록", onSuccess, onCancel},
    ref,
) {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const user = useSelector((state) => state.auth.user);
    const reload = useSelector((state) => state.api.status);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const refreshCache = useCacheRefresh();
    const fileInputRef = useRef(null);
    const signBlobRef = useRef(null);

    const [images, setImages] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [sign, setSign] = useState();
    const [showSignModal, setShowSignModal] = useState(false);
    const [roles, setRoles] = useState([]);
    const [userAccount, setUserAccount] = useState({
        serviceReceiveAuthority: false,
        emailReceiveAuthority: true,
    });
    const [nameCheckState, setNameCheckState] = useState("pending");
    const [idCheckState, setIdCheckState] = useState("pending");
    const [btnRollup, setBtnRollup] = useState(false);

    useEffect(() => {
        setNameCheckState("pending");
    }, [userAccount?.name]);

    useEffect(() => {
        setIdCheckState("pending");
    }, [userAccount?.username]);

    useEffect(() => {
        setBtnRollup(true);
        const nextRoles = Object.values(userAuth)
            .filter((each) => each.value !== "SYSTEM")
            .map((role) => ({
                checked: role.value === "USER",
                text: role.kor,
                value: role.value,
            }));
        setRoles(nextRoles);
        if (!embedded) {
            dispatch(skeleton(false));
        }
    }, [reload, dispatch, embedded]);

    const submit = useCallback(async () => {
        if (nameCheckState !== "passed") {
            alert("이름 중복확인을 해주세요");
            return;
        }
        if (idCheckState !== "passed") {
            alert("아이디 중복확인을 해주세요");
            return;
        }
        if (REQUIRED_FIELDS.some((field) => isEmpty(userAccount[field]))) {
            alert("필수값이 누락되었습니다.");
            return;
        }

        dispatch(apiLoading(true));

        const imageFile = images ? [images] : [];
        const userRoles = roles.filter((each) => each.checked).map((each) => each.value);
        const payload = {...userAccount, roles: userRoles};

        try {
            const response = await createUser(payload, imageFile);
            if (response.status === 200) {
                const newUserId = response.data?.id;
                if (signBlobRef.current && newUserId) {
                    await uploadImageSign(newUserId, userAccount.name, signBlobRef.current);
                }
                refreshCache(CacheType.userAccount);
                if (embedded) {
                    onSuccess?.(newUserId);
                } else {
                    alert("등록되었습니다.");
                    onSuccess?.(newUserId);
                }
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else if (response.response?.status === 400) {
                handleActiveError({message: "등록에 실패했습니다.", description: response.response.data.result});
            } else if (response.response?.status === 405) {
                handleActiveError({message: "이메일형식이 맞지않습니다.", description: response.response.data.result});
            } else {
                alert(response.response?.data?.message ?? "등록에 실패했습니다.");
            }
        } finally {
            dispatch(apiLoading(false));
        }
    }, [dispatch, embedded, idCheckState, images, nameCheckState, onSuccess, refreshCache, roles, userAccount]);

    useImperativeHandle(ref, () => ({submit}), [submit]);

    const checkIdIsDuplicate = async () => {
        if (!userAccount.username) {
            alert("아이디를 입력해주세요.");
            return;
        }
        const response = await usernameIsDuplicate(userAccount.username);
        if (response.status === 200) {
            if (response.data) {
                setIdCheckState("failed");
                alert("이미 사용중인 아이디입니다.");
            } else {
                setIdCheckState("passed");
                alert("사용가능한 아이디입니다.");
            }
        }
    };

    const checkNameIsDuplicate = async () => {
        if (isEmpty(userAccount.name)) {
            alert("이름을 입력해주세요");
            return;
        }
        const response = await nameIsDuplicate(userAccount.name);
        if (response.status === 200) {
            if (response.data) {
                setNameCheckState("failed");
                alert("이미 사용중인 이름입니다.");
            } else {
                setNameCheckState("passed");
                alert("사용가능한 이름입니다.");
            }
        }
    };

    const handleImageUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setImages(file);
        setImagePreviewUrl(URL.createObjectURL(file));
    };

    const handleRemoveImage = () => {
        setImages(null);
        setImagePreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSignSave = (dataURL, blob) => {
        setSign(dataURL);
        signBlobRef.current = blob;
    };

    const handleRemoveSign = () => {
        setSign(undefined);
        signBlobRef.current = null;
    };

    const handleCheckboxChange = (event, idx) => {
        setRoles((prev) =>
            prev.map((role, index) =>
                index === idx ? {...role, checked: event.target.checked} : role,
            ),
        );
    };

    const formContent = (
        <div className={`user-account-edit-form${embedded ? " user-account-edit-form--embedded" : ""}`}>
            <EditSection title="사용자 정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="업체" colspan={1} className="fieldset-td" required auth>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "company", userAccount.company, v)}
                                    data={userAccount.company}
                                    entity="company"
                                    uniqueKey="company"
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="업체명" />
                                </Selector>
                            </Th_title>
                            <Th_title title="부서" colspan={1} className="fieldset-td" required auth>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "department", userAccount.department, v)}
                                    data={userAccount.department}
                                    entity="department"
                                    uniqueKey="department"
                                    recursive
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="사용자 그룹" colspan={3} className="fieldset-td" required auth={isAdmin(user)}>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "userGroup", userAccount.userGroup, v)}
                                    data={userAccount.userGroup}
                                    entity="userGroup"
                                    uniqueKey="userGroup"
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="그룹명" />
                                </Selector>
                            </Th_title>
                        </tr>
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="기본정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="이름" colspan={1} className="fieldset-td" required auth>
                                <div className="user-account-edit-form__inline-field">
                                    <SimmsInput
                                        onChange={(v) => inputOnChange(setUserAccount, "name", v, true)}
                                        data={userAccount.name}
                                    />
                                    <button type="button" className={duplicateCheckBtnClass(nameCheckState)} onClick={checkNameIsDuplicate}>
                                        중복확인
                                    </button>
                                </div>
                            </Th_title>
                            <Th_title title="아이디" colspan={1} className="fieldset-td" required auth>
                                <div className="user-account-edit-form__inline-field">
                                    <SimmsInput
                                        onChange={(v) => inputOnChange(setUserAccount, "username", v, true)}
                                        data={userAccount.username}
                                    />
                                    <button type="button" className={duplicateCheckBtnClass(idCheckState)} onClick={checkIdIsDuplicate}>
                                        중복확인
                                    </button>
                                </div>
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="직급" colspan={1} className="fieldset-td" auth>
                                <SimmsInput onChange={(v) => inputOnChange(setUserAccount, "position", v, false)} data={userAccount.position} />
                            </Th_title>
                            <Th_title title="사번" colspan={1} className="fieldset-td" auth>
                                <SimmsInput onChange={(v) => inputOnChange(setUserAccount, "employeeNumber", v, false)} data={userAccount.employeeNumber} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="비밀번호" colspan={1} className="fieldset-td" required auth>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "password", v, true)}
                                    data={userAccount.password}
                                    constraint={{type: "password"}}
                                />
                            </Th_title>
                            <Th_title title="이메일" colspan={1} className="fieldset-td" required auth>
                                <SimmsInput onChange={(v) => inputOnChange(setUserAccount, "email", v, true)} data={userAccount.email} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="핸드폰번호" colspan={1} className="fieldset-td" auth>
                                <SimmsInput onChange={(v) => inputOnChange(setUserAccount, "phoneNumber", v, false)} data={userAccount.phoneNumber} />
                            </Th_title>
                            <Th_title title="사무실번호" colspan={1} className="fieldset-td" auth>
                                <SimmsInput onChange={(v) => inputOnChange(setUserAccount, "officeNumber", v, false)} data={userAccount.officeNumber} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="프로필 이미지" colspan={1} className="fieldset-td" auth>
                                <UserAccountProfileImageField
                                    fileInputRef={fileInputRef}
                                    fileName={images?.name ?? ""}
                                    showRemove={Boolean(images)}
                                    previewSrc={imagePreviewUrl}
                                    onFileChange={handleImageUpload}
                                    onRemove={handleRemoveImage}
                                />
                            </Th_title>
                            <Th_title title="서명등록" colspan={1} className="fieldset-td" auth>
                                <div className="user-account-edit-form__media">
                                    <button type="button" className="user-account-edit-form__media-btn" onClick={() => setShowSignModal(true)}>
                                        입력
                                    </button>
                                    {sign && (
                                        <>
                                            <UserAccountMediaRemoveButton onClick={handleRemoveSign} />
                                            <img src={sign} className="user-account-edit-form__sign" alt="" />
                                        </>
                                    )}
                                </div>
                            </Th_title>
                        </tr>
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="권한정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="이메일 수신" colspan={3} className="fieldset-td" auth>
                                <SimmsRadio
                                    options={emailReceiveAuth}
                                    onChange={(v) => inputOnChange(setUserAccount, "emailReceiveAuthority", v, true)}
                                    data={userAccount.emailReceiveAuthority}
                                />
                            </Th_title>
                        </tr>
                        {!isAdmin(user) && (
                            <>
                                {platFrom !== "IAM" && (
                                    <tr>
                                        <Th_title title="서비스접수 권한" colspan={3} className="fieldset-td" auth>
                                            <ReadOnlyInputBasic
                                                target="serviceReceiveAuthority"
                                                value={userAccount.serviceReceiveAuthority ? "활성화" : "비활성화"}
                                            />
                                        </Th_title>
                                    </tr>
                                )}
                                <tr>
                                    <Th_title title="사용자 권한" colspan={3} className="fieldset-td" auth>
                                        <ReadOnlyInputBasic target="roles" value={userAuth.USER.kor} />
                                    </Th_title>
                                </tr>
                            </>
                        )}
                        {isAdmin(user) && (
                            <>
                                {platFrom !== "IAM" && (
                                    <tr>
                                        <Th_title title="서비스접수 권한" colspan={3} className="fieldset-td" auth>
                                            <SimmsRadio
                                                options={serviceReceiveAuth}
                                                onChange={(v) => inputOnChange(setUserAccount, "serviceReceiveAuthority", v, true)}
                                                data={userAccount.serviceReceiveAuthority}
                                            />
                                        </Th_title>
                                    </tr>
                                )}
                                <tr>
                                    <Th_title title="사용자 권한" colspan={3} className="fieldset-td" auth>
                                        <div className="user-account-edit-form__roles">
                                            {roles.map((each, idx) => (
                                                <JellyCheckBox
                                                    key={each.value}
                                                    title={each.text}
                                                    checked={each.checked}
                                                    fnc={(e) => handleCheckboxChange(e, idx)}
                                                />
                                            ))}
                                        </div>
                                    </Th_title>
                                </tr>
                            </>
                        )}
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="사용기간">
                <UserAccountFormTable>
                    <tbody>
                        {!isSystem(user) && (
                            <tr>
                                <Th_title title="사용기간(단위: 일)" colspan={3} className="fieldset-td" auth>
                                    <ReadOnlyInputBasic target="usePeriodDate" value={userAccount.usePeriodDate} />
                                </Th_title>
                            </tr>
                        )}
                        {isSystem(user) && (
                            <tr>
                                <Th_title title="사용기간(단위: 일)" colspan={3} className="fieldset-td" auth>
                                    <SimmsDatePicker
                                        onChange={(v) => dateOnChange(setUserAccount, "usePeriodDate", v, false)}
                                        data={userAccount.usePeriodDate}
                                    />
                                </Th_title>
                            </tr>
                        )}
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            {showSignModal && (
                <SignaturePadModal
                    user={{name: userAccount.name}}
                    opener={setShowSignModal}
                    setter={setSign}
                    onSave={handleSignSave}
                />
            )}
        </div>
    );

    if (embedded) {
        return (
            <div className="user-account-manage-create user-account-manage-create--embedded">
                <div className="user-account-manage-create__scroll">{formContent}</div>
            </div>
        );
    }

    return (
        <>
            {formContent}
            <div className="flex justify-end gap-2 px-2 mt-2">
                <DetailSummaryButton muted onClick={() => (onCancel ? onCancel() : navigate("/settings/userAccount/list"))}>
                    취소
                </DetailSummaryButton>
                <DetailSummaryButton
                    onClick={() => {
                        if (window.confirm("등록 하시겠습니까?")) {
                            submit();
                        }
                    }}
                >
                    {submitLabel}
                </DetailSummaryButton>
            </div>
        </>
    );
});

export default UserAccountCreateForm;
